import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Copy, Check, ArrowLeft } from 'lucide-react';
import { searchCoupons } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [copiedCode, setCopiedCode] = useState(null);
  const [searchResults, setSearchResults] = useState({ stores: [], coupons: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSearchQuery(query);
    if (query.trim()) {
      performSearch(query);
    } else {
      setSearchResults({ stores: [], coupons: [] });
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const results = await searchCoupons(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code, couponId, storeDomain) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(couponId);
    toast({
      title: "Code Copied!",
      description: `Coupon code "${code}" copied to clipboard`,
    });
    // Open store website in new tab
    window.open(`https://${storeDomain}`, '_blank');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      {/* Search Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-slate-700 hover:text-slate-900 mb-6 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search for stores or coupons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg border-2 border-slate-200 focus:border-slate-900 rounded-2xl shadow-lg"
              />
            </div>
          </form>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {!query.trim() ? (
            <div className="text-center py-12">
              <p className="text-lg text-slate-600">Enter a search term to find stores and coupons</p>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-slate-900 mb-8">
                Search Results for "{query}"
              </h1>

              {/* Stores Results */}
              {searchResults.stores.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Stores ({searchResults.stores.length})</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {searchResults.stores.map(store => (
                      <Link key={store.id} to={`/store/${store.id}`}>
                        <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-slate-300">
                          <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-white shadow-md flex items-center justify-center">
                              <img 
                                src={store.logo} 
                                alt={store.name} 
                                className="w-14 h-14 object-contain" 
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name)}&background=1e293b&color=fff&size=128`;
                                }} 
                              />
                            </div>
                            <h3 className="font-semibold text-slate-900 mb-1 text-sm">{store.name}</h3>
                            <Badge className="bg-slate-100 text-slate-700 text-xs">
                              {store.totalCoupons} coupons
                            </Badge>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Coupons Results */}
              {searchResults.coupons.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Coupons ({searchResults.coupons.length})</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {searchResults.coupons.map(coupon => {
                      const storeInfo = { name: coupon.store_name, domain: coupon.store_domain, logo: coupon.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(coupon.store_name)}&background=1e293b&color=fff&size=128` };
                      return (
                        <Card key={coupon.id} className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-slate-300">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <img 
                                  src={storeInfo.logo} 
                                  alt={storeInfo.name} 
                                  className="w-12 h-12 rounded-lg object-contain" 
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(storeInfo.name || 'Store')}&background=1e293b&color=fff&size=128`;
                                  }} 
                                />
                                <div>
                                  <h3 className="font-semibold text-slate-900">{storeInfo.name}</h3>
                                  {coupon.verified && (
                                    <Badge variant="outline" className="text-xs border-green-500 text-green-700">
                                      Verified
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <Badge className="bg-slate-900 text-white font-bold">
                                {coupon.discount}
                              </Badge>
                            </div>
                            <h4 className="font-semibold text-slate-900 mb-2">{coupon.title}</h4>
                            <p className="text-sm text-slate-600 mb-4 line-clamp-2">{coupon.description}</p>
                            <div className="flex items-center justify-between">
                              {coupon.code ? (
                                <Button
                                  onClick={() => handleCopyCode(coupon.code, coupon.id, storeInfo.domain)}
                                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                  {copiedCode === coupon.id ? (
                                    <><Check className="w-4 h-4 mr-2" /> Copied!</>
                                  ) : (
                                    <><Copy className="w-4 h-4 mr-2" /> {coupon.code}</>
                                  )}
                                </Button>
                              ) : (
                                <Button 
                                  onClick={() => window.open(`https://${storeInfo.domain}`, '_blank')}
                                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                  Get Deal
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchResults.stores.length === 0 && searchResults.coupons.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No results found</h3>
                  <p className="text-slate-600">Try searching for different stores or coupon categories</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SearchPage;
